import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, ShoppingBag, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CartDrawer } from "./CartDrawer";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const ABOUT_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/team", label: "Our Team" },
  { to: "/blog", label: "Blog" },
  { to: "/news", label: "News" },
  { to: "/jobs", label: "Careers" },
];

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const doLogout = async () => { await logout(); navigate("/"); };
  const aboutActive = ABOUT_LINKS.some((l) => pathname === l.to || pathname.startsWith(l.to + "/"));

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-brand-bg/80 border-b border-brand/10">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo height={44} />
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" data-testid="nav-home"
            className={`text-sm font-medium transition-colors hover:text-brand ${pathname === "/" ? "text-brand" : "text-brand-muted"}`}>Home</Link>
          <Link to="/shop" data-testid="nav-shop"
            className={`text-sm font-medium transition-colors hover:text-brand ${pathname === "/shop" ? "text-brand" : "text-brand-muted"}`}>Shop</Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-about-trigger"
                className={`inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-brand outline-none ${aboutActive ? "text-brand" : "text-brand-muted"}`}>
                About <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-brand-bg border-brand/10">
              {ABOUT_LINKS.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} data-testid={`nav-about-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="cursor-pointer text-brand focus:bg-brand-surface focus:text-brand">{l.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/faq" data-testid="nav-faq"
            className={`text-sm font-medium transition-colors hover:text-brand ${pathname === "/faq" ? "text-brand" : "text-brand-muted"}`}>FAQ</Link>
          <Link to="/contact" data-testid="nav-contact"
            className={`text-sm font-medium transition-colors hover:text-brand ${pathname === "/contact" ? "text-brand" : "text-brand-muted"}`}>Contact</Link>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)}
            className="relative p-2.5 rounded-full hover:bg-brand-surface transition-colors">
            <ShoppingBag className="w-5 h-5 text-brand" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-brand-terracotta text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {user.role === "admin" && (
                <Button variant="ghost" size="sm" data-testid="nav-admin" onClick={() => navigate("/admin")} className="text-brand gap-1.5">
                  <LayoutDashboard className="w-4 h-4" /> Admin
                </Button>
              )}
              <Button variant="ghost" size="sm" data-testid="nav-dashboard" onClick={() => navigate("/dashboard")} className="text-brand gap-1.5">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
              </Button>
              <Button variant="ghost" size="icon" data-testid="nav-logout" onClick={doLogout} className="text-brand-muted">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button size="sm" data-testid="nav-login" onClick={() => navigate("/login")}
              className="hidden md:inline-flex bg-brand hover:bg-brand-hover text-white rounded-full px-5">
              Sign In
            </Button>
          )}

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="md:hidden p-2.5 rounded-full hover:bg-brand-surface">
                <Menu className="w-5 h-5 text-brand" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-brand-bg w-[280px] border-brand/10">
              <div className="flex flex-col gap-1 mt-8">
                {LINKS.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} onClick={() => setMenuOpen(false)}
                    className="px-3 py-3 rounded-md text-lg font-display text-brand hover:bg-brand-surface">
                    {l.label}
                  </Link>
                ))}
                <div className="h-px bg-brand/10 my-3" />
                <span className="px-3 text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">Company</span>
                {ABOUT_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setMenuOpen(false)}
                    className="px-3 py-2.5 rounded-md text-base font-display text-brand hover:bg-brand-surface">
                    {l.label}
                  </Link>
                ))}
                <div className="h-px bg-brand/10 my-3" />
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <Link to="/admin" data-testid="mobile-nav-admin" onClick={() => setMenuOpen(false)} className="px-3 py-3 rounded-md font-medium text-brand hover:bg-brand-surface flex items-center gap-2"><LayoutDashboard className="w-4 h-4" />Admin Panel</Link>
                    )}
                    <Link to="/dashboard" data-testid="mobile-nav-dashboard" onClick={() => setMenuOpen(false)} className="px-3 py-3 rounded-md font-medium text-brand hover:bg-brand-surface flex items-center gap-2"><User className="w-4 h-4" />My Account</Link>
                    <button data-testid="mobile-nav-logout" onClick={() => { setMenuOpen(false); doLogout(); }} className="px-3 py-3 rounded-md font-medium text-brand-muted hover:bg-brand-surface flex items-center gap-2 text-left"><LogOut className="w-4 h-4" />Log Out</button>
                  </>
                ) : (
                  <Link to="/login" data-testid="mobile-nav-login" onClick={() => setMenuOpen(false)} className="px-3 py-3 rounded-md font-medium text-brand hover:bg-brand-surface">Sign In</Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  );
}
